import React, {
  Dispatch,
  FC,
  memo,
  RefObject,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react'

import { useSetFocus } from '@please/lrud'
import { useNavigate } from 'react-router-dom'

import { AutoHide } from '@/components/AutoHide/AutoHide'
import { focusIds, orientation } from '@/constants/focus'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { useAdsStore } from '@/store/useAdsStore'
import useStore from '@/store/useStore'
import { VideoViewedType } from '@/types/analyticsTypes'
import { EventType } from '@/types/servicesTypes/configTypes'

import { PlaybackControls } from './PlaybackControls'
import {
  AdProgressionCounter,
  AnimationBox,
  Box,
  ControlsContainer,
  Dot,
  StyledControlsContainer,
} from './styles'
import { formatDuration } from '../ProgressBar/formatDuration'

type Props = {
  domRect?: DOMRect
  videoElementRef?: RefObject<HTMLVideoElement>
  isFullscreen: boolean
  videoData?: VideoViewedType
  setIsVideoPlaying: Dispatch<SetStateAction<boolean>>
  isVideoPlaying?: boolean
  onExitButton?: () => void
  currentEvent?: Nullable<EventType>
  focusIdPrefix?: string
  isVideoStarted?: boolean
}

const AdControlsRaw: FC<Props> = ({
  domRect,
  videoElementRef,
  isFullscreen,
  videoData,
  setIsVideoPlaying,
  isVideoPlaying = true,
  onExitButton,
  currentEvent,
  focusIdPrefix = '',
  isVideoStarted = false,
}) => {
  const setFocus = useSetFocus()
  const navigate = useNavigate()
  const { t } = useCommonTranslation()

  const { videoControlsVisible, showVideoControls, hideVideoControls } =
    useStore((state) => state.player)

  const { adControls, adProgression } = useAdsStore()

  const showVideoPlayerControls = videoElementRef

  const toggleShow = useCallback(
    (next: boolean) => {
      if (next) {
        showVideoControls()
      } else {
        hideVideoControls()
      }
    },
    [showVideoControls, hideVideoControls],
  )

  const options = useMemo(
    () => ({
      onHide: () => hideVideoControls(),
      onShow: () => showVideoControls(),
    }),
    [hideVideoControls, showVideoControls],
  )

  const onExit = (e?: LRUDEvent) => {
    hideVideoControls()
    if (onExitButton) {
      e?.stopPropagation()
      return onExitButton()
    }
    isFullscreen ? navigate(-1) : setFocus('VideoFocus')
  }

  const setPlayingHandler = (isPlaying: boolean) => {
    setIsVideoPlaying(isPlaying)
    if (isPlaying) {
      adControls?.resume()
    } else {
      adControls?.pause()
    }
  }
  return (
    <StyledControlsContainer
      $inFront={videoControlsVisible}
      $domRect={domRect}
      $isFullscreen={isFullscreen}
    >
      <AutoHide show onChange={toggleShow} options={options}>
        <ControlsContainer
          defaultFocusChild={1}
          focusId={focusIdPrefix + focusIds.VIDEO_CONTAINER}
          elementType="div"
          orientation={orientation.VERTICAL}
          $padding="6%"
          $transparent={!videoControlsVisible}
        >
          <AnimationBox $height="100%" $justifyContent="flex-end" $showDiv>
            {adProgression && (
              <AdProgressionCounter>
                <span>{t('ad')}</span>
                {formatDuration(
                  adProgression?.duration - adProgression?.currentTime,
                )}{' '}
                <Dot />{' '}
                {t('adProgress', {
                  currentAdIndex: adProgression.adPosition,
                  adsNumber: adProgression.totalAds,
                })}
              </AdProgressionCounter>
            )}
            {videoControlsVisible && showVideoPlayerControls && (
              <Box $marginTop={64} $display="block">
                <PlaybackControls
                  videoElementRef={videoElementRef}
                  videoData={videoData}
                  setIsVideoPlaying={setPlayingHandler}
                  onExit={onExit}
                  currentEvent={currentEvent}
                  isVideoPlaying={isVideoPlaying}
                  isVideoStarted={isVideoStarted}
                  showChannels={false}
                  displayLiveChannels={false}
                  focusIdPrefix={focusIdPrefix}
                  displayVideoControls={videoControlsVisible}
                />
              </Box>
            )}
          </AnimationBox>
        </ControlsContainer>
      </AutoHide>
    </StyledControlsContainer>
  )
}

export const AdControls = memo(AdControlsRaw)
